import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch, fetchWithAuth, API_ENDPOINT } from '../utils/api';
import {
  Plus, ChevronRight, ChevronDown, Trash2, Upload, FileText,
  AlertTriangle, CheckCircle, Clock, Loader, ShieldAlert, X, Save, Zap, BookOpen, RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast'; 

// ─────────────────────────────────────────────
// POETE / HAZARD CONFIG
// ─────────────────────────────────────────────
const POETE_CATEGORIES = ['Planning', 'Organization', 'Equipment', 'Training', 'Exercises'];

const POETE_CONFIG = {
  Planning:     { color: '#7c3aed', bg: '#ede9fe', icon: '📋' },
  Organization: { color: '#2563eb', bg: '#dbeafe', icon: '👥' },
  Equipment:    { color: '#d97706', bg: '#fef3c7', icon: '🔧' },
  Training:     { color: '#059669', bg: '#d1fae5', icon: '🎓' },
  Exercises:    { color: '#dc2626', bg: '#fee2e2', icon: '🏃' },
};

const HAZARD_CATEGORIES = ['Natural', 'Technological', 'Human-Caused'];
const PROBABILITY_OPTIONS = ['High', 'Medium', 'Low'];

const STATUS_CONFIG = {
  in_progress:     { label: 'In Progress',    color: 'bg-blue-100 text-blue-700',    icon: Clock },
  generating:      { label: 'Generating...', color: 'bg-yellow-100 text-yellow-700', icon: Loader },
  draft_generated: { label: 'Draft Generated', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  finalized:       { label: 'Finalized',       color: 'bg-slate-100 text-slate-700',  icon: CheckCircle },
};

// ─────────────────────────────────────────────
// API HELPERS — uses your existing api.js pattern
// GET  → fetchWithAuth(path, orgId)
// POST/PUT/DELETE → authenticatedFetch(fullUrl, options)
// organization_id always appended as query param
// ─────────────────────────────────────────────
const getOrgId = () => localStorage.getItem('organizationId');

async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const org    = getOrgId();
  const separator = path.includes('?') ? '&' : '?';
  const url = `${API_ENDPOINT}${path}${separator}organization_id=${org}`;

  if (method === 'GET') {
    // fetchWithAuth builds the URL internally the same way — pass path + orgId
    return fetchWithAuth(path, org);
  }

  // POST / PUT / DELETE — authenticatedFetch with full URL
  return authenticatedFetch(url, {
    method,
    ...options,
  });
}

// ─────────────────────────────────────────────
// SMALL SHARED COMPONENTS
// ─────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'generating' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function SectionCard({ title, subtitle, children, action }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PROJECT LIST VIEW
// ─────────────────────────────────────────────
function ProjectList({ onSelect, onNew }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/thira/projects');
      setProjects(data.projects || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this THIRA project? This cannot be undone.')) return;
    try {
      await apiFetch(`/thira/projects/${projectId}`, { method: 'DELETE' });
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
      toast.success('Project deleted');
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Loading THIRA projects...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
            <h2 className="text-xl font-semibold text-gray-900">THIRA / SPR</h2>
            <p className="text-sm text-gray-500 mt-1">
                Threat and Hazard Identification and Risk Assessment — CPG 201 compliant
            </p>
        </div>
        <div className="flex items-center gap-2">
            <button
            onClick={onNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
            >
            <Plus className="w-4 h-4" />
            New THIRA Project
            </button>
            <button
            onClick={loadProjects}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            >
            <RefreshCw className="w-4 h-4" />
            Refresh
            </button>
            </div>
     </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <ShieldAlert className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>AI-assisted THIRA generation.</strong> Hazdex pre-populates your threat/hazard list
          and capability targets from your incident history. Add community meeting notes and SME input,
          then generate a CPG 201-structured draft in minutes.
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Project list */}
      {projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No THIRA projects yet</p>
          <p className="text-gray-400 text-sm mt-1">Create your first project to get started</p>
          <button
            onClick={onNew}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
          >
            Create THIRA Project
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map(project => (
            <div
              key={project.project_id}
              onClick={() => onSelect(project)}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm cursor-pointer transition group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900 truncate">{project.title}</h3>
                    <StatusBadge status={project.status} />
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    {project.jurisdiction_name && (
                      <span>{project.jurisdiction_name}</span>
                    )}
                    <span>Planning Year: {project.planning_year}</span>
                    <span>
                      {project.hazards?.length || 0} hazard{project.hazards?.length !== 1 ? 's' : ''}
                    </span>
                    <span>
                      Updated {new Date(project.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={e => handleDelete(e, project.project_id)}
                    className="p-1.5 text-gray-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// NEW PROJECT MODAL
// ─────────────────────────────────────────────
function NewProjectModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    title: `${new Date().getFullYear()} THIRA/SPR`,
    planning_year: String(new Date().getFullYear()),
    jurisdiction_name: '',
    review_cycle: 'Annual',
  });
  const [loading, setLoading] = useState(false);
  const [contextData, setContextData] = useState(null);

  useEffect(() => {
    apiFetch('/thira/context')
      .then(d => setContextData(d))
      .catch(console.error);
  }, []);

  const handleCreate = async () => {
    if (!form.jurisdiction_name.trim()) {
      toast.error('Please enter a jurisdiction name.');
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch('/thira/projects', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      onCreate(data.project);
    } catch (e) {
      toast.error(`Failed to create project: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">New THIRA Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {contextData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <strong>Ready to pre-populate</strong> — found {contextData.total_events} events
              in your history with {Object.keys(contextData.hazard_counts || {}).length} hazard types.
              Your project will start with this data already filled in.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planning Year</label>
              <input
                type="text"
                value={form.planning_year}
                onChange={e => setForm(f => ({ ...f, planning_year: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Cycle</label>
              <select
                value={form.review_cycle}
                onChange={e => setForm(f => ({ ...f, review_cycle: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Annual</option>
                <option>Biennial</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jurisdiction Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Baltimore County, MD"
              value={form.jurisdiction_name}
              onChange={e => setForm(f => ({ ...f, jurisdiction_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm disabled:opacity-50"
          >
            {loading && <Loader className="w-4 h-4 animate-spin" />}
            Create Project
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HAZARD ROW — editable row in Step 1
// ─────────────────────────────────────────────
function HazardRow({ hazard, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
        onClick={() => setExpanded(e => !e)}
      >
        <input
          type="checkbox"
          checked={hazard.confirmed}
          onChange={e => { e.stopPropagation(); onChange({ ...hazard, confirmed: e.target.checked }); }}
          className="w-4 h-4 text-blue-600 rounded"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{hazard.display_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              hazard.category === 'Natural' ? 'bg-green-100 text-green-700' :
              hazard.category === 'Technological' ? 'bg-orange-100 text-orange-700' :
              'bg-red-100 text-red-700'
            }`}>
              {hazard.category}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              hazard.probability === 'High' ? 'bg-red-100 text-red-700' :
              hazard.probability === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {hazard.probability} probability
            </span>
            {hazard.historical_frequency > 0 && (
              <span className="text-xs text-gray-500">{hazard.historical_frequency} historical event{hazard.historical_frequency !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); onRemove(); }}
            className="text-gray-300 hover:text-red-500 transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3 border-t border-gray-100">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
              <input
                type="text"
                value={hazard.display_name}
                onChange={e => onChange({ ...hazard, display_name: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={hazard.category}
                onChange={e => onChange({ ...hazard, category: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                {HAZARD_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Probability</label>
              <select
                value={hazard.probability}
                onChange={e => onChange({ ...hazard, probability: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                {PROBABILITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Local Context / SME Notes (CPG 201 Step 2)
            </label>
            <textarea
              value={hazard.sme_context}
              onChange={e => onChange({ ...hazard, sme_context: e.target.value })}
              placeholder="Describe local factors, vulnerable populations, critical infrastructure at risk, historical impacts..."
              rows={3}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// DOCUMENT UPLOADER — Step 2 supporting docs
// ─────────────────────────────────────────────
function DocumentUploader({ projectId, documents, onDocumentsChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      for (const file of files) {
        // 1. Get presigned upload URL
        const urlData = await apiFetch(`/thira/projects/${projectId}/upload-url`, {
          method: 'POST',
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            doc_type: 'supporting',
          }),
        });

        // 2. Upload directly to S3
        await fetch(urlData.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        // 3. Register the document in the project
        const regData = await apiFetch(`/thira/projects/${projectId}/documents`, {
          method: 'POST',
          body: JSON.stringify({
            s3_key: urlData.s3_key,
            filename: file.name,
            doc_type: 'supporting',
          }),
        });

        onDocumentsChange(prev => [...prev, regData.document]);
        toast.success(`${file.name} uploaded successfully`);
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await apiFetch(`/thira/projects/${projectId}/documents/${docId}`, { method: 'DELETE' });
      onDocumentsChange(prev => prev.filter(d => d.doc_id !== docId));
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500 mb-1">
          Upload community meeting notes, SME questionnaires, prior THIRAs, or demographic data
        </p>
        <p className="text-xs text-gray-400 mb-3">PDF, Word, or text files</p>
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm font-medium">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Choose Files'}
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.csv"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.doc_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-700 flex-1 truncate">{doc.filename}</span>
              <span className="text-xs text-gray-400">
                {new Date(doc.uploaded_at).toLocaleDateString()}
              </span>
              <button
                onClick={() => handleDelete(doc.doc_id)}
                className="text-gray-300 hover:text-red-500 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// PROJECT WORKSPACE — the main editing view
// ─────────────────────────────────────────────
function ProjectWorkspace({ project: initialProject, onBack }) {
  const [project, setProject]     = useState(initialProject);
  const [activeStep, setActiveStep] = useState('hazards');
  const [saving, setSaving]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const steps = [
    { key: 'hazards',      label: 'Step 1 — Threats & Hazards' },
    { key: 'documents',    label: 'Step 2 — Context & Documents' },
    { key: 'capabilities', label: 'Step 3 — Capability Targets' },
    { key: 'generate',     label: 'Step 4 — Generate & Review' },
  ];

  // Poll for status update while generating
  useEffect(() => {
    if (project.status !== 'generating') return;
    const interval = setInterval(async () => {
      try {
        const data = await apiFetch(`/thira/projects/${project.project_id}`);
        setProject(data.project);
        if (data.project.status !== 'generating') {
          clearInterval(interval);
          setGenerating(false);
          setActiveStep('generate');
          toast.success('Your THIRA draft is ready!');
        }
      } catch (e) {
        console.error('Poll error:', e);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [project.status, project.project_id]);

  useEffect(() => {
  const reloadProject = async () => {
    try {
      const data = await apiFetch(`/thira/projects/${project.project_id}`);
      if (data.project) {
        setProject(data.project);
        // If draft exists, go straight to generate tab
        if (data.project.generated_draft && data.project.status === 'draft_generated') {
          setActiveStep('generate');
        }
      }
    } catch (e) {
      console.error('Failed to reload project:', e);
    }
  };
  reloadProject();
}, []); // runs once on mount

  const handleSave = async (updates = {}) => {
    try {
      setSaving(true);
      const merged = { ...project, ...updates };
      await apiFetch(`/thira/projects/${project.project_id}`, {
        method: 'PUT',
        body: JSON.stringify(merged),
      });
      setProject(merged);
      toast.success('Project saved');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    const confirmed = project.hazards?.filter(h => h.confirmed);
    if (!confirmed?.length) {
        toast.error('Please confirm at least one hazard before generating.');
        return;
    }
    if (!window.confirm(
        'Generate the THIRA draft? This will use Bedrock to synthesize your data and may take 30–60 seconds.'
    )) return;

        try {
            setGenerating(true);
            // Save current state first
            await apiFetch(`/thira/projects/${project.project_id}`, {
            method: 'PUT',
            body: JSON.stringify(project),
            });
            // Trigger async generation — returns 202 immediately
            await apiFetch(`/thira/projects/${project.project_id}/generate`, {
            method: 'POST',
            });
            // Update local status to generating — polling useEffect takes over
            toast.success('Generating your THIRA draft — this takes 30–60 seconds');
            setProject(p => ({ ...p, status: 'generating' }));
        } catch (e) {
            // 202 comes back as success in authenticatedFetch so errors here are real failures
            toast.error(`Generation failed: ${e.message}`);
            setGenerating(false);
        }
    };

  const handleExport = async (format = 'docx') => {
    try {
        setExporting(true);
        const data = await apiFetch(
        `/thira/projects/${project.project_id}/document?format=${format}`
        );
        // Open the presigned URL to trigger download
        window.open(data.download_url, '_blank');
        toast.success(`${format.toUpperCase()} download started`);
    } catch (e) {
        toast.error(`Export failed: ${e.message}`);
    } finally {
        setExporting(false);
    }
  };

  const updateHazard = (index, updated) => {
    const hazards = [...(project.hazards || [])];
    hazards[index] = updated;
    setProject(p => ({ ...p, hazards }));
  };

  const removeHazard = (index) => {
    const hazards = project.hazards.filter((_, i) => i !== index);
    setProject(p => ({ ...p, hazards }));
  };

  const addHazard = () => {
    const newHazard = {
      hazard_id:              `manual-${Date.now()}`,
      hazard_type:            'custom',
      display_name:           'New Hazard',
      category:               'Natural',
      historical_frequency:   0,
      probability:            'Low',
      magnitude:              '',
      affected_area:          '',
      vulnerable_populations: '',
      critical_infrastructure:'',
      sme_context:            '',
      confirmed:              false,
    };
    setProject(p => ({ ...p, hazards: [...(p.hazards || []), newHazard] }));
  };

  const confirmedCount = (project.hazards || []).filter(h => h.confirmed).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={onBack}
            className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-2 transition"
          >
            ← Back to projects
          </button>
          <h2 className="text-xl font-semibold text-gray-900">{project.title}</h2>
          <div className="flex items-center gap-3 mt-1">
            {project.jurisdiction_name && (
              <span className="text-sm text-gray-500">{project.jurisdiction_name}</span>
            )}
            <StatusBadge status={project.status} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave()}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
          >
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* Step tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {steps.map(step => (
          <button
            key={step.key}
            onClick={() => setActiveStep(step.key)}
            className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition ${
              activeStep === step.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {step.label}
          </button>
        ))}
      </div>

      {/* ── STEP 1: HAZARDS ── */}
      {activeStep === 'hazards' && (
        <SectionCard
          title="Threats & Hazards"
          subtitle={`Pre-populated from your event history. Confirm the hazards to include and add local context.`}
          action={
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{confirmedCount} confirmed</span>
              <button
                onClick={addHazard}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-xs font-medium"
              >
                <Plus className="w-3.5 h-3.5" /> Add Hazard
              </button>
            </div>
          }
        >
          {(project.hazards || []).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">
              No hazards found. Your organization may not have events yet, or add hazards manually.
            </p>
          ) : (
            <div className="space-y-2">
              {project.hazards.map((hazard, i) => (
                <HazardRow
                  key={hazard.hazard_id}
                  hazard={hazard}
                  onChange={updated => updateHazard(i, updated)}
                  onRemove={() => removeHazard(i)}
                />
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ── STEP 2: DOCUMENTS ── */}
      {activeStep === 'documents' && (
        <SectionCard
          title="Context & Supporting Documents"
          subtitle="Upload community meeting notes, prior THIRAs, demographic data, or SME questionnaires. These will be read and incorporated into your THIRA draft."
        >
          <DocumentUploader
            projectId={project.project_id}
            documents={project.supporting_documents || []}
            onDocumentsChange={docs => {
              const updated = typeof docs === 'function'
                ? docs(project.supporting_documents || [])
                : docs;
              setProject(p => ({ ...p, supporting_documents: updated }));
            }}
          />
        </SectionCard>
      )}

      {/* ── STEP 3: CAPABILITY TARGETS ── */}
      {activeStep === 'capabilities' && (
        <SectionCard
          title="Capability Targets"
          subtitle="Pre-filled from your historical gap data. Review and adjust before generating."
        >
          <div className="space-y-4">
            {POETE_CATEGORIES.map(cat => {
              const cfg = POETE_CONFIG[cat];
              return (
                <div key={cat} className="border rounded-lg p-4" style={{ borderColor: cfg.color + '30' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span>{cfg.icon}</span>
                    <span className="font-medium text-gray-900 text-sm">{cat}</span>
                  </div>
                  <textarea
                    placeholder={`Describe your ${cat.toLowerCase()} capability targets and gaps for this planning cycle...`}
                    value={project.capability_targets?.[cat] || ''}
                    onChange={e => setProject(p => ({
                      ...p,
                      capability_targets: { ...(p.capability_targets || {}), [cat]: e.target.value }
                    }))}
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* ── STEP 4: GENERATE & REVIEW ── */}
      {activeStep === 'generate' && (
        <div className="space-y-4">
        
            {/* ── No draft yet — show generate panel ── */}
            {!project.generated_draft && project.status !== 'generating' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Generate THIRA Draft</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                    Hazdex will synthesize your event history, confirmed hazards, uploaded
                    documents, and capability data into a CPG 201-compliant draft.
                </p>
                </div>
                <div className="p-6 space-y-4">
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-2xl font-semibold text-gray-900">{confirmedCount}</p>
                    <p className="text-xs text-gray-500 mt-1">Confirmed hazards</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-2xl font-semibold text-gray-900">
                        {(project.supporting_documents || []).length}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Supporting documents</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-2xl font-semibold text-gray-900">
                        {Object.values(project.capability_targets || {}).filter(v => v).length}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Capability targets added</p>
                    </div>
                </div>
        
                {confirmedCount === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    ⚠️ No hazards confirmed yet. Go back to Step 1 and check the hazards
                    you want included.
                    </div>
                )}
        
                <button
                    onClick={handleGenerate}
                    disabled={generating || confirmedCount === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
                >
                    {generating
                    ? <><Loader className="w-5 h-5 animate-spin" /> Generating — this takes 30–60 seconds...</>
                    : <><Zap className="w-5 h-5" /> Generate THIRA Draft</>
                    }
                </button>
                </div>
            </div>
            )}
        
            {/* ── Generating spinner ── */}
            {project.status === 'generating' && (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <Loader className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
                <p className="text-gray-700 font-medium">Generating your THIRA draft...</p>
                <p className="text-gray-400 text-sm mt-2">
                Bedrock is synthesizing your data. This typically takes 30–60 seconds.
                </p>
            </div>
            )}
        
            {/* ── Draft exists — show Actions & Reports style panel ── */}
            {project.generated_draft && project.status !== 'generating' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Actions &amp; Reports</h3>
                <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-xs font-medium disabled:opacity-50"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${generating ? 'animate-spin' : ''}`} />
                    {generating ? 'Regenerating...' : 'Regenerate THIRA'}
                </button>
                </div>
        
                <div className="p-6">
                {/* AI disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 mb-6">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                    <strong>AI-generated draft.</strong> Review all content with qualified
                    emergency management professionals before official submission or use.
                    </p>
                </div>
        
                {/* Two-column layout matching AAR panel */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
                    {/* Left — document info */}
                    <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Generated Report</h4>
                    <div className="space-y-3 text-sm text-gray-600">
                        <div>
                        <span className="font-medium text-gray-700">Generated:</span>
                        <p className="text-gray-900">
                            {new Date(project.updated_at).toLocaleString()}
                        </p>
                        </div>
                        <div>
                        <span className="font-medium text-gray-700">Jurisdiction:</span>
                        <p className="text-gray-900">{project.jurisdiction_name}</p>
                        </div>
                        <div>
                        <span className="font-medium text-gray-700">Planning Year:</span>
                        <p className="text-gray-900">{project.planning_year}</p>
                        </div>
                        <div>
                        <span className="font-medium text-gray-700">Framework:</span>
                        <p className="text-gray-900">FEMA CPG 201</p>
                        </div>
                        <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <p className="text-green-600 flex items-center gap-1 mt-0.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Draft ready for review
                        </p>
                        </div>
                    </div>
                    </div>
        
                    {/* Right — download buttons matching AAR style */}
                    <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Download Report</h4>
                    <div className="grid grid-cols-2 gap-3">
                        {/* PDF */}
                        <button
                        onClick={() => handleExport('pdf')}
                        disabled={exporting}
                        className="flex flex-col items-center justify-center p-4 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition group disabled:opacity-50"
                        >
                        {exporting
                            ? <Loader className="w-8 h-8 text-red-400 mb-2 animate-spin" />
                            : <FileText className="w-8 h-8 text-red-600 mb-2" />
                        }
                        <span className="font-medium text-red-700 text-sm">PDF</span>
                        <span className="text-xs text-red-600 group-hover:underline mt-1">
                            Download
                        </span>
                        </button>
        
                        {/* Word */}
                        <button
                        onClick={() => handleExport('docx')}
                        disabled={exporting}
                        className="flex flex-col items-center justify-center p-4 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition group disabled:opacity-50"
                        >
                        {exporting
                            ? <Loader className="w-8 h-8 text-blue-400 mb-2 animate-spin" />
                            : <FileText className="w-8 h-8 text-blue-600 mb-2" />
                        }
                        <span className="font-medium text-blue-700 text-sm">Word</span>
                        <span className="text-xs text-blue-600 group-hover:underline mt-1">
                            Download
                        </span>
                        </button>
                    </div>
        
                    <p className="text-xs text-gray-400 mt-3 text-center">
                        Word document is fully editable. PDF is formatted for submission.
                    </p>
                    </div>
                </div>
                </div>
            </div>
            )}
        </div>
        )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN EXPORT — THIRAView
// ─────────────────────────────────────────────
export default function THIRAView() {
  const [view, setView]             = useState('list');   // 'list' | 'workspace'
  const [selectedProject, setSelectedProject] = useState(null);
  const [showNewModal, setShowNewModal]       = useState(false);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setView('workspace');
  };

  const handleNewProject = () => setShowNewModal(true);

  const handleProjectCreated = (project) => {
    setShowNewModal(false);
    setSelectedProject(project);
    setView('workspace');
    toast.success('THIRA project created — hazards pre-populated from your event history');
  };

  const handleBack = () => {
    setSelectedProject(null);
    setView('list');
  };

  return (
    <div>
      {view === 'list' && (
        <ProjectList onSelect={handleSelectProject} onNew={handleNewProject} />
      )}

      {view === 'workspace' && selectedProject && (
        <ProjectWorkspace project={selectedProject} onBack={handleBack} />
      )}

      {showNewModal && (
        <NewProjectModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleProjectCreated}
        />
      )}
    </div>
  );
}